import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find, toNumber } from 'lodash-es'
import { v4 } from 'uuid'
import { TabPanel } from '@mui/lab'
import { Typography } from '@mui/material'
import { Stack } from '@mui/system'
import {
  wallSettingsOf,
  WallSettings,
} from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeleteButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import {
  useEditElementStore,
  useControlStore,
  useModelStore,
  useSelectionStore,
} from '@editorStores'
import { useBlockScene, useResultsInvalidation, useSceneElement } from '@editorHooks'
import {
  AssemblyAssignmentForm,
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleOpeningsBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  CollapsibleSplitBoxSection,
} from '@editorComponents'
import { useModelInvalidation, useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel, getElementLoads } from '@queries'
import { createWall, deleteWall, updateWall as updateWallRequest } from '@mutations'
import { buildErrorMessage } from 'src/constants'
import CopyToOtherStoreys from '../../../MultiEdit/components/CopyToOtherStoreys'
import PlanSettingsForm from '../../../PlanSettingsForm'
import SplitWallForm from '../../../SplitWallForm'
import WallGeometryForm from '../../../WallGeometryForm'
import { toNewWallPosition } from '../../../WallGeometryForm/utils'
import WallOpeningsSection from '../../../WallOpeningsSection'
import WallDrawer2D from '../WallDrawer2D'
import { WallFormBase } from './components'

interface Props {
  selectedElement: string | null
  geometryEditable?: boolean
  storeySelected: boolean
  onClose: () => void
}

/**
 * 2D wall pane. It owns the whole wall edit state: the draw tool (registered as a scene element
 * while the wall drawing mode is active), the drawn draft and the geometry form, which renders its
 * own state into the scene (`WallFormScene`). Nothing unsaved is ever written to the model store.
 */
const WallForm = ({
  selectedElement,
  geometryEditable = true,
  storeySelected,
  onClose,
}: Props): ReactElement | null => {
  const [draft, setDraft] = useState<Wall | null>(null)

  const walls = useModelStore(state => state.model.walls)
  const removeWall = useModelStore(state => state.removeWall)

  const mode = useSelectionStore(state => state.mode)
  const storey = useControlStore(state => state.floorplanState.storey)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()

  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const wall = useMemo(() => {
    return find(walls, ['guid', selectedElement]) as Wall | undefined
  }, [walls, selectedElement])

  const elementType: ElementTypes = wall?.placement === 'Internal' ? 'inner_walls' : 'outer_walls'

  const queryClient = useQueryClient()

  // Selecting a (saved) wall drops the draft instead of keeping it around for later. Drawing
  // deselects, which keeps the new draft.
  useEffect(() => {
    if (selectedElement) setDraft(null)
  }, [selectedElement])

  const onWallDrawn = useCallback(
    (newDraft: Wall) => {
      setDraft(newDraft)
      setActiveElement(null)
    },
    [setActiveElement],
  )

  const drawer = useMemo(
    () => ({
      enabled: mode === 'draw-walls' && !!storey,
      properties: { activeStorey: storey as string, onWallDrawn },
      component: WallDrawer2D,
    }),
    [mode, storey, onWallDrawn],
  )

  useSceneElement(drawer)

  const discardDraft = () => setDraft(null)

  useStructuralPlanningFormEsc(() => {
    // an unsaved draft is dropped
    if (draft) setDraft(null)
    onClose()
  }, !!selectedElement || !!draft)

  // MUTATIONS

  const { mutateAsync: handleCreate, isLoading } = useMutation(
    (wall: Wall) =>
      createWall.request(projectId, {
        guid: wall.guid,
        p1: wall.shape.points[0],
        p2: wall.shape.points[1],
        placement: wall.placement,
        storey_idx: toNumber(wall.storey),
        ...(wall.label ? { label: wall.label } : {}),
        ...wallSettingsOf(wall),
      }),
    {
      onSuccess: async (_response, createdWall) => {
        // The refetched model already contains the new wall in store shape (`inner_walls` /
        // `outer_walls` element_type, string storey). Merging the raw response DTO on top of it
        // would overwrite `element_type` with the backend's generic "walls", which no visibility
        // filter knows, so the wall would be hidden until the next reload.
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        setDraft(null)
        setActiveElement(createdWall.guid)
        enqueueSnackbar('Wand erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (wallGuid: string) => deleteWall.request(projectId, wallGuid),
    {
      onSuccess: async (_response, wallGuid) => {
        await invalidateModel(projectId as string)
        setActiveElement(null)
        removeWall(wallGuid)
        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleUpdate, isLoading: isUpdating } = useMutation(
    async ({
      submittedWall,
      dependentWalls,
    }: {
      submittedWall: ShapeObject
      dependentWalls: ShapeObject[]
    }) => {
      await updateWallRequest.request(
        projectId,
        submittedWall.guid,
        toNewWallPosition(submittedWall),
      )

      for (const dependentWall of dependentWalls) {
        await updateWallRequest.request(
          projectId,
          dependentWall.guid,
          toNewWallPosition(dependentWall),
        )
      }

      return submittedWall
    },
    {
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        setActiveElement(null)

        await queryClient.invalidateQueries(getElementLoads.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))

        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich verändert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern der Wand'), {
          variant: 'error',
        })
        // the main wall may have been saved before a dependent wall failed: restore the server state
        invalidateModel(projectId as string)
      },
    },
  )

  useBlockScene(isDeleting || isLoading || isUpdating)

  // Handlers for split wall form
  const handleCreateWall = async (
    wallData: {
      p1: { x: number; y: number }
      p2: { x: number; y: number }
      storey_idx: number
      placement: WallPlacement
      openings?: Opening[]
    } & WallSettings,
  ) => {
    const newWall = await createWall.request(projectId, {
      guid: v4(),
      p1: wallData.p1,
      p2: wallData.p2,
      storey_idx: wallData.storey_idx,
      placement: wallData.placement as 'Internal',
      positionPlanSettings: wallData.positionPlanSettings,
      certificateSettings: wallData.certificateSettings,
    })
    await invalidateModel(projectId as string)
    invalidateResults(projectId as string)
    return newWall
  }

  const handleDeleteWall = async (wallGuid: string) => {
    await deleteWall.request(projectId, wallGuid)
    removeWall(wallGuid)
    await invalidateModel(projectId as string)
    invalidateResults(projectId as string)
  }

  if (!storeySelected) return null

  if (draft && !selectedElement)
    return (
      <Stack direction="column" spacing={1} p={1}>
        <WallGeometryForm
          wall={draft}
          isDraft
          sceneEditEnabled
          handleSubmit={submittedWall =>
            handleCreate({ ...wallSettingsOf(draft), ...(submittedWall as Wall) })
          }
          handleDelete={discardDraft}
          isSubmitting={isLoading}
        />
      </Stack>
    )

  if (!wall)
    return (
      <Box p={1} border={1} borderColor="grey.200" borderRadius={1} mb={2}>
        <Typography mb={1}>Sie haben nun die Möglichkeit neue Wände einzuzeichnen</Typography>
      </Box>
    )

  return (
    // the pane stays mounted for the draft, but each wall's sections start with fresh state
    <WallFormBase key={wall.guid} initialTabValue="geometry">
      <TabPanel value="geometry" sx={{ padding: 1 }}>
        {geometryEditable ? (
          <>
            <Stack direction="column" spacing={0.5}>
              <WallGeometryForm
                wall={wall}
                sceneEditEnabled
                handleSubmit={(submittedWall, dependentWalls) =>
                  handleUpdate({ submittedWall, dependentWalls })
                }
                handleDelete={() => handleDelete(wall.guid)}
                isDeleting={isDeleting}
                isSubmitting={isUpdating}
                onClose={onClose}
              />

              <CollapsibleAssemblyBoxSection defaultExpanded={true}>
                <Stack direction="column" spacing={1}>
                  <AssemblyAssignmentForm
                    elementType={elementType}
                    selectedElements={[wall.guid]}
                    onClose={onClose}
                    showSelectedElementsNumber={false}
                    autoFocus={false}
                  />
                </Stack>
              </CollapsibleAssemblyBoxSection>

              <CollapsibleOpeningsBoxSection defaultExpanded={false}>
                <WallOpeningsSection wall={wall} mode="2D" />
              </CollapsibleOpeningsBoxSection>

              <CollapsibleProblemsBoxSection elementGuid={wall.guid} defaultExpanded={true} />

              <CollapsibleSettingsBoxSection defaultExpanded={false}>
                <PlanSettingsForm element={wall} />
              </CollapsibleSettingsBoxSection>

              <CollapsibleSplitBoxSection label="Teilen" defaultExpanded={false}>
                <SplitWallForm
                  element_guid={wall.guid}
                  handleCreate={handleCreateWall}
                  handleDelete={handleDeleteWall}
                />
              </CollapsibleSplitBoxSection>
              {wall.storey && (
                <CollapsibleCopyBoxSection
                  label="Kopieren"
                  defaultExpanded={false}
                  dataCy="wall-form-copy-to-storey"
                >
                  <CopyToOtherStoreys selectedElements={[wall.guid]} storey={wall.storey} />
                </CollapsibleCopyBoxSection>
              )}
            </Stack>
          </>
        ) : (
          <DeleteButton
            onClick={() => handleDelete(wall.guid)}
            loading={isDeleting}
            data-cy="delete-wall-btn"
          >
            Löschen
          </DeleteButton>
        )}
      </TabPanel>
    </WallFormBase>
  )
}

export default WallForm
